import { useState, useEffect, useCallback, useRef } from 'react';
import { AUTH_CONFIG } from './authConfig';

const STORAGE_KEY  = 'swiso_auth_v1';
const ATTEMPT_KEY  = 'swiso_auth_attempts';
const LOCKOUT_KEY  = 'swiso_auth_lockout';

// ── SHA-256 via Web Crypto API ────────────────────────────────────
async function sha256(text) {
  const buf  = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map(x => x.toString(16).padStart(2, '0')).join('');
}

// ── Session persistence ───────────────────────────────────────────
function loadSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    const now = Date.now();
    if (now - session.loginAt > AUTH_CONFIG.SESSION_DURATION_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch { return null; }
}

function saveSession() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ loginAt: Date.now(), authenticated: true }));
  } catch {}
}

function clearSession() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

// ── Attempt / lockout helpers ─────────────────────────────────────
function getAttempts() {
  try { return parseInt(localStorage.getItem(ATTEMPT_KEY) || '0', 10); } catch { return 0; }
}
function setAttempts(n) {
  try { localStorage.setItem(ATTEMPT_KEY, String(n)); } catch {}
}
function getLockoutUntil() {
  try { return parseInt(localStorage.getItem(LOCKOUT_KEY) || '0', 10); } catch { return 0; }
}
function setLockoutUntil(ts) {
  try { localStorage.setItem(LOCKOUT_KEY, String(ts)); } catch {}
}
function clearAttempts() {
  try { localStorage.removeItem(ATTEMPT_KEY); localStorage.removeItem(LOCKOUT_KEY); } catch {}
}

// ── Main hook ─────────────────────────────────────────────────────
export function useAuth() {
  const [authenticated, setAuthenticated] = useState(() => !!loadSession());
  const [error, setError]                 = useState('');
  const [attempts, setAttemptsState]      = useState(() => getAttempts());
  const [lockedUntil, setLockedUntil]     = useState(() => getLockoutUntil());
  const [loading, setLoading]             = useState(false);
  const activityTimer = useRef(null);

  // ── Activity timeout — reset on any user interaction ──
  const resetActivityTimer = useCallback(() => {
    if (activityTimer.current) clearTimeout(activityTimer.current);
    activityTimer.current = setTimeout(() => {
      clearSession();
      setAuthenticated(false);
      setError('Your session has expired due to inactivity. Please sign in again.');
    }, AUTH_CONFIG.SESSION_DURATION_MS);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetActivityTimer, { passive: true }));
    resetActivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivityTimer));
      if (activityTimer.current) clearTimeout(activityTimer.current);
    };
  }, [authenticated, resetActivityTimer]);

  // ── Lockout countdown ──
  useEffect(() => {
    if (lockedUntil <= Date.now()) return;
    const interval = setInterval(() => {
      if (Date.now() >= lockedUntil) {
        setLockedUntil(0);
        clearInterval(interval);
      } else {
        setLockedUntil(getLockoutUntil());
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  // ── Login ──
  const login = useCallback(async (password) => {
    setError('');

    // Check lockout
    const lockUntil = getLockoutUntil();
    if (lockUntil > Date.now()) {
      const secsLeft = Math.ceil((lockUntil - Date.now()) / 1000);
      setError(`Too many failed attempts. Try again in ${secsLeft} seconds.`);
      return false;
    }

    setLoading(true);
    try {
      const hash = await sha256(password.trim());
      if (hash === AUTH_CONFIG.PASSWORD_HASH) {
        clearAttempts();
        saveSession();
        setAuthenticated(true);
        setAttemptsState(0);
        setLockedUntil(0);
        setError('');
        return true;
      } else {
        const newAttempts = getAttempts() + 1;
        setAttempts(newAttempts);
        setAttemptsState(newAttempts);

        const remaining = AUTH_CONFIG.MAX_ATTEMPTS - newAttempts;

        if (newAttempts >= AUTH_CONFIG.MAX_ATTEMPTS) {
          const lockTs = Date.now() + AUTH_CONFIG.LOCKOUT_MS;
          setLockoutUntil(lockTs);
          setLockedUntil(lockTs);
          setAttempts(0);
          setAttemptsState(0);
          const mins = Math.round(AUTH_CONFIG.LOCKOUT_MS / 60000);
          setError(`Too many failed attempts. Access locked for ${mins} minutes.`);
        } else {
          setError(`Incorrect password. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
        }
        return false;
      }
    } catch (e) {
      setError('An error occurred. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Logout ──
  const logout = useCallback(() => {
    clearSession();
    setAuthenticated(false);
    setError('');
  }, []);

  const isLocked      = lockedUntil > Date.now();
  const lockSecsLeft  = isLocked ? Math.ceil((lockedUntil - Date.now()) / 1000) : 0;
  const sessionTimeLeft = authenticated ? (() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return s.loginAt ? Math.max(0, AUTH_CONFIG.SESSION_DURATION_MS - (Date.now() - s.loginAt)) : 0;
    } catch { return 0; }
  })() : 0;

  return { authenticated, login, logout, error, loading, attempts, isLocked, lockSecsLeft, sessionTimeLeft };
}
